import { ErrorHandler, inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import * as StackTrace from 'stacktrace-js';
import * as Sentry from '@sentry/browser';
import { environment } from '../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { OverlayContainer } from '@angular/cdk/overlay';
import { friendlyExternalMessage } from './external-error';
import { TranslationService } from './service/translation-service.service';
import { NotificationService } from './service/notification.service';

@Injectable()
export class CustomErrorHandler implements ErrorHandler {
  private zone = inject(NgZone);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private translation = inject(TranslationService);
  private notification = inject(NotificationService);
  private dialog = inject(MatDialog, { optional: true });
  private overlayContainer = inject(OverlayContainer, { optional: true });
  private recentErrors = new Map<string, number>();

  handleError(error: any) {
    if (!error) return;
    if (error.rejection) {
      error = error.rejection;
    }

    const key = this.errorKey(error);
    const now = Date.now();
    if (now - (this.recentErrors.get(key) ?? 0) < 5000) return;
    this.recentErrors.set(key, now);

    this.handleErrorAsync(error);
  }

  private async handleErrorAsync(error: any): Promise<void> {
    try {
      await this.doHandleError(error);
    } catch {
      const fallback = await this.translation
        .loadFromBackend('error_handler_fallback_tr')
        .catch(() => 'Something went wrong while processing the error. Please reload the page and try again.');
      this.showToast(fallback);
    }
  }

  private async doHandleError(error: any): Promise<void> {
    if (error?.message?.startsWith('Failed to fetch dynamically imported module')) {
      this.zone.runOutsideAngular(() => location.reload());
      return;
    }

    if (this.isHttpError(error)) {
      if (environment.sentryDsn && error.status !== 401) {
        Sentry.withScope((scope) => {
          scope.setLevel(error.status >= 500 ? 'error' : 'warning');
          scope.setTag('http.status_code', error.status.toString());
          scope.setTag('http.url', error.url || 'unknown');
          scope.setContext('http', { url: error.url, status_code: error.status, response: error.error });
          scope.setContext('page', { url: this.router.url });
          scope.setFingerprint(['http-error', error.status.toString(), error.url || 'unknown']);
          const httpError = new Error(`HTTP ${error.status}: ${error.error?.message || error.message}`);
          httpError.name = 'HttpError';
          Sentry.captureException(httpError);
        });
      }
      const friendly = friendlyExternalMessage((error as HttpErrorResponse).error?.error_type);
      this.showToast(navigator.onLine ? (friendly ?? (await this.formatHttpError(error))) : 'No Internet Connection');
      return;
    }

    if (this.isComponentDefinitionError(error)) {
      if (environment.sentryDsn) Sentry.captureException(error);
      this.showToast(
        'We were unable to open the requested screen. Please reload and try again. If this keeps happening contact support.'
      );
      return;
    }

    if (await this.isThirdPartyLibraryError(error)) {
      if (environment.sentryDsn) Sentry.captureException(error);
      void this.notification.errorWithSupport('third_party_library_error_tr', undefined, {
        subject: 'third_party_library_error_tr',
        body: [`Page: ${window.location.href}`, error?.stack ?? error?.message].filter(Boolean).join('\n\n'),
      });
      return;
    }

    if (environment.sentryDsn) Sentry.captureException(error);
    if (error instanceof Error) {
      this.showError(error.name || 'Error', error.message || 'An unknown error occurred', error);
      return;
    }
    this.showToast('An unexpected error occurred. Please reload and try again.');
  }

  private isComponentDefinitionError(error: any): boolean {
    if (!(error instanceof TypeError)) return false;
    const message = error.message ?? '';
    return (
      message.includes('ɵcmp') ||
      message.includes('Cannot read properties of undefined') ||
      message.includes('getComponentDef')
    );
  }

  private async isThirdPartyLibraryError(error: any): Promise<boolean> {
    const visited = new Set<any>();
    const pending = [error];
    const details: unknown[] = [];

    while (pending.length && visited.size < 5) {
      const current = pending.pop();
      if (!current || (typeof current !== 'object' && typeof current !== 'function') || visited.has(current)) continue;
      visited.add(current);
      details.push(current.name, current.message, current.stack);
      pending.push(current.cause, current.originalError, current.rejection);
    }

    const stackAndMessage = details.filter((value): value is string => typeof value === 'string').join('\n').toLowerCase();
    if (
      /(?:node_modules|webpack:\/\/.*node_modules|vendor(?:\.|\/|\\)|mapbox|@turf\/|\bturf\b|pdfjs-dist|ng2-pdf-viewer|unknownerrorexception|getraw is not a function)/i.test(
        stackAndMessage
      )
    ) {
      return true;
    }

    try {
      const frames = await StackTrace.fromError(error);
      return frames.some((frame) => this.isThirdPartyFrame(frame.fileName));
    } catch {
      return false;
    }
  }

  private isThirdPartyFrame(fileName?: string | null): boolean {
    if (!fileName) return false;
    const path = fileName.toLowerCase().replaceAll('\\', '/');
    if (/(?:^|\/)src\/app\//.test(path) || /(?:^|\/)src\/main\.ts/.test(path)) return false;
    if (/(?:node_modules|webpack:\/\/.*node_modules|vendor(?:\.|\/)|mapbox|@turf\/|\bturf\b)/i.test(path)) return true;

    try {
      const frameUrl = new URL(fileName, window.location.href);
      return /^https?:$/.test(frameUrl.protocol) && frameUrl.origin !== window.location.origin;
    } catch {
      return false;
    }
  }

  private async resolveStackFrames(error: any): Promise<string | null> {
    try {
      const stackFrames = await StackTrace.fromError(error);
      const relevant = stackFrames.filter((sf) => !sf.fileName?.includes('node_modules')).slice(0, 5);
      const frames = relevant.length > 0 ? relevant : stackFrames.slice(0, 3);
      return frames.length > 0 ? frames.map((sf) => sf.toString()).join('\n') : null;
    } catch {
      return null;
    }
  }

  private showToast(message: string) {
    this.zone.run(() => this.toastr.error(message, undefined, { disableTimeOut: true, enableHtml: true }));
  }

  private showError(title: string, message: string, rawError?: any) {
    this.dialog && this.zone.run(() => this.dialog!.closeAll());
    const host = this.overlayContainer?.getContainerElement();
    if (host) host.textContent = '';

    let container = document.getElementById('error-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'error-container';
      document.body.appendChild(container);

      const header = document.createElement('div');
      const h1 = document.createElement('h1');
      h1.textContent = 'Internal application error';
      header.appendChild(h1);

      const actions = document.createElement('div');
      actions.classList.add('error-header-actions');

      const returnBtn = document.createElement('button');
      returnBtn.classList.add('mat-support-button');
      returnBtn.textContent = 'Return to dashboard';
      returnBtn.addEventListener('click', () => {
        container?.remove();
        this.zone.run(() => this.router.navigateByUrl('/'));
      });

      const supportBtn = document.createElement('a');
      supportBtn.classList.add('mat-support-button');
      supportBtn.setAttribute('role', 'button');
      supportBtn.target = '_blank';
      supportBtn.href = this.buildMailtoLink(title, message);
      const icon = document.createElement('span');
      icon.className = 'material-icons support-link-icon';
      icon.textContent = 'support_agent';
      const label = document.createElement('span');
      label.textContent = 'Contact support';
      supportBtn.appendChild(icon);
      supportBtn.appendChild(label);

      actions.appendChild(returnBtn);
      actions.appendChild(supportBtn);
      header.appendChild(actions);
      container.appendChild(header);
    }

    const h2 = document.createElement('h2');
    h2.textContent = `${title}: ${message}`;
    container.appendChild(h2);
    container.appendChild(this.renderMetadata(rawError));

    if (rawError?.stack) {
      const pre = document.createElement('pre');
      pre.textContent = rawError.stack;
      container.appendChild(pre);
    }

    const supportLink = container.querySelector('.error-header-actions a');
    if (supportLink instanceof HTMLAnchorElement) {
      supportLink.href = this.buildMailtoLink(title, message, rawError?.stack);
    }

    // Fire-and-forget async stack frame resolution via stacktrace-js (source-mapped).
    // Appends a second <pre> and updates the support mailto once resolved.
    if (rawError) {
      this.resolveStackFrames(rawError).then((resolved) => {
        if (!resolved) return;
        const resolvedPre = document.createElement('pre');
        resolvedPre.classList.add('error-stack-resolved');
        resolvedPre.textContent = `Resolved stack (source maps):\n${resolved}`;
        container!.appendChild(resolvedPre);
        if (supportLink instanceof HTMLAnchorElement) {
          supportLink.href = this.buildMailtoLink(title, message, `${rawError?.stack ?? ''}\n\nResolved:\n${resolved}`);
        }
      });
    }
  }

  private errorKey(error: any): string {
    if (this.isHttpError(error)) return `http:${error.status}:${error.url}`;
    return `${error.name || 'Error'}:${error.message || ''}`;
  }

  private isHttpError(error: any): error is HttpErrorResponse {
    return error instanceof HttpErrorResponse || (error != null && typeof error.status === 'number' && 'url' in error);
  }

  private async formatHttpError(error: HttpErrorResponse): Promise<string> {
    if (error.status === 403) {
      return await this.formatAccessDeniedError(error);
    }

    const detail = typeof error.error === 'string' ? error.error : JSON.stringify(error.error ?? {}, undefined, 2);
    const path = error.url ?? 'unknown endpoint';
    const page = window.location.href;
    const status = error.status ? ` (HTTP ${error.status})` : '';
    const mailto = this.buildMailtoLink(`HTTP ${error.status}`, `${path}\n${detail}`);
    const escaped = this.escapeHtml(detail);

    return `<div>
      <p>We couldn't finish that action${status} on <strong>${this.escapeHtml(path)}</strong> while on <strong>${this.escapeHtml(page)}</strong>.</p>
      <pre class="pre">${escaped}</pre>
      <div class="support-link-container">
        <a href="${mailto}" class="mat-support-button" role="button">
          <span class="material-icons support-link-icon">support_agent</span>
          <span>Contact support</span>
        </a>
      </div>
    </div>`;
  }

  private async formatAccessDeniedError(error: HttpErrorResponse): Promise<string> {
    const detail = typeof error.error === 'string' ? error.error : JSON.stringify(error.error ?? {}, undefined, 2);
    const path = error.url ?? 'unknown endpoint';
    const mailto = this.buildMailtoLink('Access denied', `${path}\n${detail}`);
    const [title, description, guidance, contactSupport] = await Promise.all([
      this.translation.loadFromBackend('access_denied_title_tr'),
      this.translation.loadFromBackend('access_denied_description_tr'),
      this.translation.loadFromBackend('access_denied_guidance_tr'),
      this.translation.loadFromBackend('Contact_Support_tr'),
    ]);

    return `<div>
      <p><strong>${this.escapeHtml(title)}</strong></p>
      <p>${this.escapeHtml(description)} ${this.escapeHtml(guidance)}</p>
      <div class="support-link-container">
        <a href="${mailto}" class="mat-support-button" role="button">
          <span class="material-icons support-link-icon">support_agent</span>
          <span>${this.escapeHtml(contactSupport)}</span>
        </a>
      </div>
    </div>`;
  }

  private buildMailtoLink(title: string, detail: string, stack?: string): string {
    const body = [
      'Hello Supplycanvas Support,',
      '',
      `Error: ${title}`,
      `Page: ${window.location.href}`,
      `Route: ${this.router.url}`,
      '',
      detail,
      stack ? `\nStack:\n${stack}` : '',
    ].join('\n');
    return `mailto:support@supplycanvas.com?subject=${encodeURIComponent(`Support: ${title}`)}&body=${encodeURIComponent(body)}`;
  }

  private renderMetadata(error?: any): HTMLElement {
    const entries: Record<string, string> = {
      Route: this.router.url,
      Location: window.location.href,
      Timestamp: new Date().toISOString(),
      'Error type': error?.constructor?.name ?? typeof error,
    };
    if (this.isHttpError(error)) {
      entries['HTTP status'] = `${error.status}`;
      entries['Endpoint'] = error.url ?? 'unknown';
    }
    const dl = document.createElement('dl');
    dl.classList.add('error-context');
    for (const [key, value] of Object.entries(entries)) {
      const dt = document.createElement('dt');
      dt.textContent = key;
      const dd = document.createElement('dd');
      dd.textContent = value;
      dl.appendChild(dt);
      dl.appendChild(dd);
    }
    return dl;
  }

  private escapeHtml(s: string): string {
    const div = document.createElement('div');
    div.textContent = s;
    return div.innerHTML;
  }
}
